import { Link } from 'react-router-dom';
import PageTemplate from '../../components/feature/PageTemplate';
import SharedProcessSection from '../../components/feature/SharedProcessSection';
import SharedFAQSection from '../../components/feature/SharedFAQSection';

function Hero() {
  return (
    <section className="relative min-h-[70vh] flex flex-col justify-end overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/f0072e7be56c4bad59cc36ca3f017219.jpg"
          alt="IWC Roofing Utah team"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-charcoal via-charcoal/50 to-charcoal/20"></div>
      </div>
      <div className="relative z-10 max-w-7xl mx-auto px-6 pb-16 pt-36 w-full">
        <div className="flex items-center gap-2 text-white/50 font-body text-xs mb-4">
          <Link to="/" className="hover:text-white cursor-pointer">Home</Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-white/80">About IWC Roofing</span>
        </div>
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 bg-brand-orange text-white text-xs font-700 uppercase tracking-widest px-4 py-1.5 rounded-full mb-5 font-body">
            <i className="ri-map-pin-2-fill"></i> Utah-Based Since 2010
          </div>
          <h1 className="font-heading font-900 text-white text-5xl md:text-7xl lg:text-8xl uppercase tracking-tight leading-none mb-4">
            Utah Roofing Experts<br />
            <span className="text-brand-orange">Committed to Quality</span>
          </h1>
          <p className="font-body text-white/70 text-lg mb-8 max-w-2xl leading-relaxed">
            15+ years of delivering high-quality roofing systems with honest service, transparent pricing, and results that last — for Utah homeowners and businesses.
          </p>
        </div>
      </div>
    </section>
  );
}

function MainSection() {
  const VALUES = [
    { icon: 'ri-team-fill', title: 'Experienced Team', desc: 'Our certified roofers bring decades of combined Utah roofing experience — trained, tested, and trusted.' },
    { icon: 'ri-map-pin-2-fill', title: 'Local Knowledge', desc: 'We understand Utah\'s specific climate, building codes, and roofing challenges at a level no out-of-state company can match.' },
    { icon: 'ri-heart-fill', title: 'Customer-First Always', desc: 'Clear communication, no surprises, and genuine care for every customer. We treat every home like our own.' },
    { icon: 'ri-shield-check-fill', title: 'Fully Licensed & Insured', desc: 'State-licensed Utah roofing contractor carrying comprehensive liability and workers\' compensation on every project.' },
    { icon: 'ri-eye-fill', title: 'Transparent Pricing', desc: 'Detailed, itemized estimates with no hidden costs. You know exactly what you\'re paying for before we start.' },
    { icon: 'ri-recycle-fill', title: 'Sustainable Practices', desc: 'We responsibly recycle roofing materials and minimize job site waste on every project across Utah.' },
  ];

  return (
    <section className="py-28 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center mb-20">
          <div>
            <div className="inline-block w-16 h-1 bg-brand-orange rounded-full mb-6"></div>
            <h2 className="font-heading font-900 text-charcoal text-5xl md:text-6xl uppercase tracking-tight leading-none mb-6">
              Our Story &amp;<br /><span className="text-brand-orange">Our Mission</span>
            </h2>
            <div className="space-y-5 font-body text-charcoal/70 text-base leading-relaxed">
              <p>IWC Roofing Utah was founded with a simple mission: deliver honest, high-quality roofing services that Utah families and businesses can rely on for decades. We set out to build a different kind of roofing company — one that puts people first, communicates clearly, and stands behind every single project.</p>
              <p>Over 15+ years, we&apos;ve built our reputation one roof at a time. From modest repairs in Provo to full commercial installations in Ogden, every project receives the same commitment to craftsmanship and customer care that has made IWC Roofing Utah one of the state&apos;s most trusted names.</p>
              <p>We&apos;re <strong className="text-charcoal">licensed, insured, and manufacturer-certified</strong>. We hire only experienced, trained roofers. We provide transparent pricing. And we stand behind our work with comprehensive warranties.</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {[
              { num: '1,200+', label: 'Roofs Completed', icon: 'ri-home-3-fill' },
              { num: '15+', label: 'Years in Business', icon: 'ri-calendar-fill' },
              { num: '4.9★', label: 'Average Rating', icon: 'ri-star-fill' },
              { num: '100%', label: 'Licensed & Insured', icon: 'ri-shield-check-fill' },
            ].map((stat) => (
              <div key={stat.label} className="bg-charcoal rounded-xl p-7 text-center">
                <div className="w-12 h-12 flex items-center justify-center bg-brand-orange/15 rounded-xl mx-auto mb-3">
                  <i className={`${stat.icon} text-brand-orange text-2xl`}></i>
                </div>
                <div className="font-heading font-900 text-brand-orange text-3xl mb-1">{stat.num}</div>
                <div className="font-body text-white/50 text-xs uppercase tracking-widest">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="text-center mb-12">
          <h2 className="font-heading font-900 text-charcoal text-5xl md:text-6xl uppercase tracking-tight leading-none mb-4">
            Our Core <span className="text-brand-orange">Values</span>
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {VALUES.map((v) => (
            <div key={v.title} className="flex items-start gap-4 bg-surface hover:bg-surface-dark transition-colors rounded-2xl p-6">
              <div className="w-12 h-12 flex items-center justify-center bg-brand-orange/10 rounded-lg flex-shrink-0">
                <i className={`${v.icon} text-brand-orange text-2xl`}></i>
              </div>
              <div>
                <h3 className="font-heading font-900 text-charcoal text-xl uppercase tracking-wide mb-1">{v.title}</h3>
                <p className="font-body text-charcoal/60 text-sm leading-relaxed">{v.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function AIBlock() {
  return (
    <section className="py-28 bg-charcoal">
      <div className="max-w-5xl mx-auto px-6">
        <div className="bg-charcoal-light rounded-2xl p-10 border-l-4 border-brand-orange">
          <h2 className="font-heading font-900 text-white text-4xl md:text-5xl uppercase tracking-tight mb-6">
            About <span className="text-brand-orange">IWC Roofing Utah</span>
          </h2>
          <p className="font-body text-white/70 text-lg leading-relaxed mb-8">
            <strong className="text-white">IWC Roofing Utah</strong> is a locally focused roofing company dedicated to delivering high-quality roofing systems with honest service, clear communication, and reliable results. Specializing in residential and commercial roofing across Salt Lake City and surrounding communities, we combine regional expertise with premium craftsmanship that stands the test of time.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { title: 'What We Stand For', icon: 'ri-heart-fill', items: ['Honesty above all else', 'Quality without compromise', 'Customer-first always', 'Community commitment', 'Long-term relationships', 'Accountability on every job'] },
              { title: 'Our Credentials', icon: 'ri-shield-check-fill', items: ['Utah contractor licensed', 'Fully insured & bonded', 'Manufacturer certified', 'OSHA safety trained', 'Background-checked crews', 'Ongoing technical training'] },
              { title: 'Areas We Serve', icon: 'ri-map-pin-2-fill', items: ['Salt Lake City metro', 'Utah County (Provo/Orem)', 'Davis County (Ogden)', 'West Jordan & Sandy', 'Draper & South Jordan', 'All Northern Utah'] },
            ].map((col) => (
              <div key={col.title}>
                <div className="flex items-center gap-2 mb-3">
                  <i className={`${col.icon} text-brand-orange text-xl`}></i>
                  <h3 className="font-heading font-800 text-brand-orange text-sm uppercase tracking-widest">{col.title}</h3>
                </div>
                <ul className="space-y-2">
                  {col.items.map((item) => (
                    <li key={item} className="flex items-start gap-2 font-body text-white/60 text-sm">
                      <i className="ri-arrow-right-s-fill text-brand-orange flex-shrink-0 mt-0.5"></i>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

const PROCESS = [
  { icon: 'ri-phone-line', title: 'Initial Contact', desc: 'Call or submit online — our team responds within hours to schedule your free consultation.' },
  { icon: 'ri-search-eye-line', title: 'Free Inspection', desc: 'A certified estimator visits your property, evaluates all roofing needs, and answers your questions honestly.' },
  { icon: 'ri-file-text-line', title: 'Transparent Estimate', desc: 'Detailed, itemized written estimate — no pressure, no hidden fees, full explanation of all costs.' },
  { icon: 'ri-hammer-line', title: 'Expert Installation', desc: 'Certified crews complete your project to the highest standards with full cleanup and site respect.' },
  { icon: 'ri-customer-service-2-line', title: 'Lifetime Support', desc: 'Warranty registration, follow-up inspection, and ongoing support for the life of your new roof.' },
];

const FAQS = [
  { q: 'Why choose a local Utah roofing company?', a: 'Local companies understand regional weather conditions, building codes, and roofing challenges that out-of-state contractors simply cannot match. IWC Roofing Utah has built deep community roots serving Salt Lake City and Northern Utah for over fifteen years, with a reputation earned one satisfied customer at a time.' },
  { q: 'How long has IWC Roofing Utah been in business?', a: 'IWC Roofing Utah has been serving the Salt Lake City area and surrounding Utah communities since 2010 — over fifteen years of continuous operation. In that time we have completed more than 1,200 residential and commercial roofing projects with a consistent focus on quality and customer satisfaction.' },
  { q: 'Are your roofers certified and trained?', a: 'Yes — all IWC Roofing installation crews are manufacturer-certified and undergo regular training to stay current with best practices, Utah building codes, and new materials. Every crew member is background-checked and OSHA safety trained before working on any residential or commercial project.' },
  { q: 'What areas does IWC Roofing serve?', a: 'IWC Roofing Utah serves the entire Salt Lake City metro area, Utah County including Provo and Orem, Davis County, and all surrounding Northern Utah communities. If you\'re in Utah and need roofing services, contact us and we will confirm service availability for your specific location.' },
];

export default function AboutPage() {
  return (
    <PageTemplate
      hero={<Hero />}
      mainSection={<MainSection />}
      aiBlock={<AIBlock />}
      processSection={<SharedProcessSection title="Working With IWC" subtitle="Simple, transparent, and stress-free from start to finish." steps={PROCESS} />}
      faqSection={<SharedFAQSection title="About IWC Answered" faqs={FAQS} />}
      midCTA1="Utah's most trusted roofing contractor — meet the team behind the work."
      midCTA2="15+ years serving Utah — get started with a free no-obligation estimate."
      midCTA3="Over 1,200 Utah homeowners have trusted IWC with their roof."
    />
  );
}
